import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import { SolutionPageStructuredData } from "../../config/StructuredData";

const TaxConsultantsPage = () => (
  <>
    <SolutionPageStructuredData
      name="Workflow Management Software for Tax Consultants"
      description="Simplifyte helps tax consultants manage client-wise income tax and GST workflows, document collection, deadline reminders, review approvals and closure proof — replacing WhatsApp and Excel chaos."
      url="https://www.simplifyte.com/solutions/tax-consultants"
    />
    <HeaderMenu />

    <main style={{ paddingTop: "80px", minHeight: "100vh" }}>
      <section style={{ background: "linear-gradient(135deg,#093A5B 0%,#0a4d6e 100%)", padding: "60px 24px", color: "#fff", textAlign: "center" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <p style={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, marginBottom: "16px" }}>Solutions / Tax Consultants</p>
          <h1 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, lineHeight: 1.2, marginBottom: "20px" }}>
            Workflow Management Software for Tax Consultants
          </h1>
          <p style={{ fontSize: "18px", opacity: 0.85, maxWidth: "700px", margin: "0 auto 32px" }}>
            Manage hundreds of clients' income tax and GST filings without losing track. Simplifyte gives tax consultants client-wise work tracking, period-based workflows, document collection, deadline reminders and closure proof in one place.
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>Book a Demo</Link>
            <Link to="/pricing" style={{ border: "1px solid rgba(255,255,255,0.5)", color: "#fff", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>View Pricing</Link>
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, color: "#093A5B", marginBottom: "24px" }}>What tax consultants manage with Simplifyte</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "20px" }}>
            {[
              { title: "ITR filings by client and AY", desc: "Track every client's income tax return by assessment year — from document collection to filing confirmation." },
              { title: "GST returns by period", desc: "Manage GSTR-1, GSTR-3B, and annual returns for all clients by month, quarter, and year." },
              { title: "TDS compliance tracking", desc: "Track TDS deduction, payment, and return filing deadlines for each client with automated reminders." },
              { title: "Document pending list", desc: "Know exactly which Form 16, bank statements, or investment proofs are pending from which client." },
              { title: "Client communication log", desc: "Every document request, reminder, and follow-up is logged — replacing scattered WhatsApp threads." },
              { title: "Closure proof per filing", desc: "Generate timestamped proof that each return was filed and acknowledged — for your records and theirs." },
            ].map((f, i) => (
              <div key={i} style={{ background: "#f8fafc", padding: "20px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <CheckCircle2 size={22} color="#093A5B" style={{ marginBottom: "10px" }} />
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{f.title}</h3>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, color: "#093A5B", marginBottom: "32px", textAlign: "center" }}>Before Simplifyte / With Simplifyte</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            <div style={{ background: "#fff5f5", padding: "24px", borderRadius: "10px" }}>
              <h3 style={{ color: "#ef4444", marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}>Before Simplifyte</h3>
              {["Filing status tracked in Excel sheets","Reminders sent manually on WhatsApp","Documents scattered across email and Drive","Assessment year confusion across clients","No proof of what was filed when"].map((t,i)=>(<p key={i} style={{ fontSize: "14px", color: "#64748b", marginBottom: "10px", paddingLeft: "8px", borderLeft: "2px solid #fca5a5" }}>{t}</p>))}
            </div>
            <div style={{ background: "#f0fdf4", padding: "24px", borderRadius: "10px" }}>
              <h3 style={{ color: "#16a34a", marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}>With Simplifyte</h3>
              {["All filings tracked by client, AY, and period","Automated reminders to staff and clients","All documents requested and tracked in one place","AY and period tags on every work item","Timestamped filing and delivery proof"].map((t,i)=>(<p key={i} style={{ fontSize: "14px", color: "#64748b", marginBottom: "10px", paddingLeft: "8px", borderLeft: "2px solid #86efac" }}>{t}</p>))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px", textAlign: "center", background: "#093A5B", color: "#fff" }}>
        <h2 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "16px" }}>Manage all your clients' tax work without the chaos</h2>
        <p style={{ opacity: 0.8, marginBottom: "28px", fontSize: "16px" }}>Book a 20-minute demo and see how Simplifyte works for tax consultants.</p>
        <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "14px 32px", borderRadius: "8px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>
          Book a Demo <ArrowRight size={16} />
        </Link>
      </section>
    </main>

    <CustomFooter />
  </>
);

export default TaxConsultantsPage;
